import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Show, SignIn, SignUp, useClerk } from "@clerk/react";
import { toast } from "sonner";
import { errMsg, fmtDate, inr } from "@/lib/api";
import { useAccountApi } from "@/lib/account";
import { WRAP } from "@/components/store/Chrome";
import { Input } from "@/components/ui/input";

const EMPTY_ADDR = { label: "", name: "", phone: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "India", default: false };
const PAY = { paid: "Paid", pending: "Awaiting payment", expired: "Payment expired" };

export function SignInPage() {
  return <div className="min-h-[70vh] flex items-center justify-center py-12" data-testid="sign-in-page"><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></div>;
}

export function SignUpPage() {
  return <div className="min-h-[70vh] flex items-center justify-center py-12" data-testid="sign-up-page"><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></div>;
}

function OrderCard({ o }) {
  const [open, setOpen] = useState(true);
  return (
    <li className="border border-[#e3e3e3] bg-white" data-testid={`account-order-${o.order_number}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex flex-wrap items-center justify-between gap-3 p-4 text-left">
        <span><b className="font-arial">{o.order_number}</b><span className="text-xs text-[#777] ml-3">{fmtDate(o.created_at)}</span></span>
        <span className="text-sm font-arial">{PAY[o.payment_status] || o.payment_status} · <b>{o.stage_name}</b> · <span className="text-red font-bold">{inr(o.total)}</span></span>
      </button>
      {open && (
        <div className="border-t border-[#eee] p-4 text-sm font-arial space-y-2">
          {o.items.map((i, n) => (
            <div key={n} className="flex items-center gap-3" data-testid={`account-order-item-${o.order_number}-${n}`}>
              <div className="w-16 h-16 shrink-0 bg-[#f2f2f2] border border-[#e3e3e3] overflow-hidden">
                {i.image ? <img src={i.image} alt={i.title} className="w-full h-full object-cover" loading="lazy" data-testid={`account-order-item-image-${o.order_number}-${n}`} /> : <div className="w-full h-full rounded-full bg-black scale-75" />}
              </div>
              <p>{i.artist} – {i.title} <span className="text-[#888]">({i.format || "LP"}) × {i.qty} · {inr(i.price)}{i.status === "not_processed" ? " · not processed" : ""}</span></p>
            </div>
          ))}
          <p className="text-[#555]">Delivery: {o.address.line1}, {o.address.city} {o.address.postal_code}</p>
          {o.tracking_url && <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-red underline">Track parcel{o.courier ? ` (${o.courier})` : ""}</a>}
          {o.payment_status === "pending" && <Link to={`/pay/${o.id}?email=${encodeURIComponent(o.customer.email)}`} className="text-red underline block">Complete payment</Link>}
        </div>
      )}
    </li>
  );
}

function AddressForm({ a, onSave, onCancel }) {
  const [f, setF] = useState(a);
  const inp = (k, ph) => <Input value={f[k]} placeholder={ph} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="rounded-none bg-white h-10" data-testid={`account-addr-${k}`} />;
  return (
    <div className="grid sm:grid-cols-2 gap-3 border border-black p-4 bg-white" data-testid="account-address-form">
      {inp("label", "Label (Home, Office…)")}{inp("name", "Full name")}
      <div className="sm:col-span-2">{inp("line1", "Address line 1")}</div>
      <div className="sm:col-span-2">{inp("line2", "Address line 2 (optional)")}</div>
      {inp("city", "City")}{inp("state", "State")}{inp("postal_code", "PIN code")}{inp("phone", "Phone")}
      <label className="sm:col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={f.default} onChange={(e) => setF({ ...f, default: e.target.checked })} data-testid="account-addr-default" /> Use as my default address</label>
      <div className="sm:col-span-2 flex gap-2 justify-end">
        <button onClick={onCancel} className="h-10 px-4 border border-black text-xs font-bold uppercase">Cancel</button>
        <button onClick={() => onSave(f)} className="h-10 px-5 bg-black text-white text-xs font-bold uppercase hover:bg-red" data-testid="account-addr-save">Save address</button>
      </div>
    </div>
  );
}

function Dashboard() {
  const call = useAccountApi();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [orders, setOrders] = useState(null);
  const [tab, setTab] = useState("orders");
  const [edit, setEdit] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    call("get", "/me").then((r) => { setMe(r.data); return call("get", "/orders"); }).then((r) => setOrders(r.data)).catch((e) => setErr(errMsg(e)));
  }, [call]);
  const save = async (patch) => {
    try {
      const r = await call("put", "/me", { name: me.name, phone: me.phone, addresses: me.addresses, ...patch });
      setMe(r.data);
      setEdit(null);
      toast.success("Saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (err) return <p className="py-24 text-center" data-testid="account-error">{err}</p>;
  if (!me) return <div className="h-96 animate-pulse" />;
  const saveAddr = (a) => save({ addresses: (edit.i == null ? [...me.addresses, a] : me.addresses.map((x, n) => (n === edit.i ? a : x))).map((x) => (a.default && x !== a ? { ...x, default: false } : x)) });
  const tabCls = (t) => `px-4 h-10 text-xs font-bold uppercase tracking-[0.08em] border-b-2 ${tab === t ? "border-red text-red" : "border-transparent hover:text-red"}`;
  return (
    <div data-testid="account-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="label-caps text-[11px] text-red">My account</p><h1 className="font-head font-extrabold text-3xl sm:text-4xl tracking-tight mt-1">Hi {me.name || "there"}</h1><p className="text-sm text-[#666] mt-1 font-arial">{me.email}</p></div>
        <button onClick={() => { navigate("/"); signOut({ redirectUrl: "/" }); }} className="h-10 px-5 border border-black text-xs font-bold uppercase hover:bg-black hover:text-white" data-testid="account-sign-out">Sign out</button>
      </div>
      <div className="flex gap-2 border-b border-[#ddd] mt-8 mb-6">
        {[["orders", "Orders"], ["addresses", "Addresses"], ["profile", "Profile"]].map(([t, l]) => <button key={t} onClick={() => setTab(t)} className={tabCls(t)} data-testid={`account-tab-${t}`}>{l}</button>)}
      </div>
      {tab === "orders" && (orders?.length ? <ul className="space-y-3" data-testid="account-orders">{orders.map((o) => <OrderCard key={o.id} o={o} />)}</ul>
        : <p className="text-[#666]" data-testid="account-no-orders">No orders yet. <Link to="/shop" className="text-red underline">Start digging</Link></p>)}
      {tab === "addresses" && (
        <div className="space-y-3 max-w-2xl">
          {me.addresses.map((a, i) => (
            <div key={i} className="border border-[#e3e3e3] bg-white p-4 flex justify-between gap-4 text-sm font-arial" data-testid={`account-address-${i}`}>
              <p>{a.label && <b>{a.label} </b>}{a.default && <span className="text-[10px] uppercase bg-red text-white px-1.5 py-0.5 ml-1">Default</span>}<br />{a.name}<br />{a.line1}{a.line2 && `, ${a.line2}`}<br />{a.city}{a.state && `, ${a.state}`} {a.postal_code}</p>
              <span className="flex gap-3 text-xs shrink-0"><button onClick={() => setEdit({ i, a })} className="underline" data-testid={`account-address-edit-${i}`}>Edit</button><button onClick={() => save({ addresses: me.addresses.filter((_, n) => n !== i) })} className="underline text-red" data-testid={`account-address-delete-${i}`}>Delete</button></span>
            </div>
          ))}
          {edit ? <AddressForm a={edit.a} onSave={saveAddr} onCancel={() => setEdit(null)} /> : <button onClick={() => setEdit({ i: null, a: { ...EMPTY_ADDR, name: me.name } })} className="h-10 px-5 bg-black text-white text-xs font-bold uppercase hover:bg-red" data-testid="account-address-add">Add address</button>}
        </div>
      )}
      {tab === "profile" && <Profile me={me} onSave={save} />}
    </div>
  );
}

function Profile({ me, onSave }) {
  const [f, setF] = useState({ name: me.name, phone: me.phone });
  return (
    <div className="grid gap-3 max-w-md font-arial">
      <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Full name" className="rounded-none bg-white h-11" data-testid="account-profile-name" />
      <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Mobile number" className="rounded-none bg-white h-11" data-testid="account-profile-phone" />
      <p className="text-xs text-[#777]">To change your email or password, use "Forgot password?" on the sign-in page.</p>
      <button onClick={() => onSave(f)} className="h-11 bg-black text-white text-xs font-bold uppercase hover:bg-red" data-testid="account-profile-save">Save profile</button>
    </div>
  );
}

export default function Account() {
  return (
    <div className={`${WRAP} py-12`}>
      <Show when="signed-in"><Dashboard /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </div>
  );
}
